import React, { useEffect, useState } from "react";
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Camera,
  Save,
  Settings,
  Shield,
  Trash2,
} from "lucide-react";
import { fetchUserDetails, updateDetails } from "./fetchUserDetails";
import toast, { Toaster } from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ChangePassword from "./changePassword";

interface profileData {
  createAt: string;
  updateAt: string;
  emailVerified: boolean;
  termsAndCondition: boolean;
  subscribeNewsletter: boolean;
  firstname: string;
  lastname: string;
  phone: string;
  useremail: string;
  profileIcon: string;
  dob: string;
  bio: string;
  location: any;
  _id: string;
}

interface userData {
  user: profileData;
  message: string;
}

type TabType = "profile" | "password";

export default function ProfileSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get active tab from URL or default to 'profile'
  const activeTab = (searchParams.get("tab") as TabType) || "profile";

  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    location: "",
    bio: "",
    profileImage: "",
    emailVerified: false,
    _id: "",
  });

  const [originalProfile, setOriginalProfile] = useState({
    email: "",
    phone: "",
  });

  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [isRemovingImage, setIsRemovingImage] = useState<boolean>(false);

  // Tab configuration
  const tabs = [
    {
      id: "profile" as TabType,
      label: "Profile Information",
      icon: Settings,
      color: "teal",
    },
    {
      id: "password" as TabType,
      label: "Change Password",
      icon: Shield,
      color: "red",
    },
  ];

  const handleTabChange = (tabId: TabType) => {
    setSearchParams({ tab: tabId });
  };

  const handleInputChange = (field: any, value: any) => {
    // Only allow numbers and max 10 digits for phone
    if (field === "phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setProfile((prev) => ({ ...prev, phone: numericValue }));
      return;
    }

    // Validate date of birth
    if (field === "dateOfBirth" && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      
      // Check if date is in the future
      if (selectedDate > today) {
        toast.error("Date of birth cannot be in the future");
        return;
      }
      
      // Check if user is at least 13 years old
      const age = today.getFullYear() - selectedDate.getFullYear();
      const monthDiff = today.getMonth() - selectedDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < selectedDate.getDate()) 
        ? age - 1 
        : age;
      
      if (actualAge < 13) {
        toast.error("You must be at least 13 years old");
        return;
      }
      
      // Check if not too old (more than 120 years)
      if (actualAge > 120) {
        toast.error("Please enter a valid date of birth");
        return;
      }
    }
    
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Allowed image formats
      const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      // Check file type
      if (!allowedFormats.includes(file.type)) {
        toast.error('Only JPG, JPEG, PNG, GIF, and WebP formats are allowed');
        // Reset file input
        e.target.value = '';
        setProfileImageFile(null);
        setImagePreview("");
        return;
      }

      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('Image size must be less than 5MB');
        e.target.value = '';
        setProfileImageFile(null);
        setImagePreview("");
        return;
      }

      // Store the original File object
      setProfileImageFile(file);

      // Create preview URL for display
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      toast.success(`Image selected: ${file.name}`);
    }
  };

  const handleRemoveImage = async () => {
    // If a new local file was chosen but not saved yet, cancel it and restore current image
    if (profileImageFile) {
      setProfileImageFile(null);
      setImagePreview(profile.profileImage || "");
      toast.success("Image selection cancelled");
      return;
    }

    if (!profile.profileImage && !imagePreview) {
      return;
    }

    if (!window.confirm("Are you sure you want to remove your profile photo?")) {
      return;
    }

    setIsRemovingImage(true);
    const formData = new FormData();
    formData.append("fullName", profile.fullName || "");
    formData.append("email", profile.email || "");
    formData.append("phone", profile.phone ? profile.phone.trim() : "");
    formData.append("dateOfBirth", profile.dateOfBirth || "");
    formData.append("location", profile.location ? profile.location.trim() : "");
    formData.append("bio", profile.bio ? profile.bio.trim() : "");
    formData.append("removeProfileImage", "true");

    try {
      const res: any = await updateDetails(formData);
      if (res.message === "Profile updated successfully") {
        toast.success("Profile photo removed successfully");
        setProfileImageFile(null);
        setImagePreview("");
        setProfile((prev) => ({ ...prev, profileImage: "" }));
        await handleFetchDetails();
        window.dispatchEvent(new Event('auth-change'));
      } else {
        toast.error(res?.message || "Failed to remove photo");
      }
    } catch (err: any) {
      toast.error(err?.message || "An unexpected error occurred");
    } finally {
      setIsRemovingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate phone number if entered
    if (profile.phone && profile.phone.trim()) {
      const cleanPhone = profile.phone.trim();
      if (!/^\d{10}$/.test(cleanPhone)) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
    }

    const formData = new FormData();
    formData.append("fullName", profile.fullName || "");
    formData.append("email", profile.email || "");
    formData.append("phone", profile.phone ? profile.phone.trim() : "");
    formData.append("dateOfBirth", profile.dateOfBirth || "");
    formData.append("location", profile.location ? profile.location.trim() : "");
    formData.append("bio", profile.bio ? profile.bio.trim() : "");
    if (profileImageFile) {
      formData.append("profileImage", profileImageFile);
    } else if (!imagePreview && profile.profileImage) {
      formData.append("removeProfileImage", "true");
    }

    try {
      const res: any = await updateDetails(formData);
      
      if (res.message === "Profile updated successfully") {
        toast.success("Profile updated successfully");
        // Reset image file after successful upload
        setProfileImageFile(null);
        // Reset preview to show newly fetched image
        setImagePreview("");
        // Refresh user details
        await handleFetchDetails();
        window.dispatchEvent(new Event('auth-change'));
        // Redirect to profile page
        setTimeout(() => {
          navigate("/profile");
        }, 600);
      } else {
        const errorMessage = typeof res.message === "string" 
          ? res.message 
          : "Failed to update profile";
        toast.error(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = typeof err === "string" 
        ? err 
        : (err?.message || "An unexpected error occurred");
      toast.error(errorMessage);
    }
  };

  const handleFetchDetails = async () => {
    try {
      const data = (await fetchUserDetails()) as userData;
      
      // Check for invalid token or missing user data
      if (data.message === "Invalid token" || !data.user) {
        navigate("/", { replace: true });
        return;
      }

      // Ensure date is in YYYY-MM-DD format for HTML date input
      let formattedDob = data.user.dob || "";
      if (formattedDob && !formattedDob.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // If date is in DD-MM-YYYY format, convert it
        const dateParts = formattedDob.split("-");
        if (dateParts.length === 3) {
          const day = dateParts[0];
          const month = dateParts[1];
          const year = dateParts[2];
          formattedDob = `${year}-${month}-${day}`;
        }
      }

      const rawUser = data.user || ({} as any);

      const cleanPhone =
        rawUser.phone && rawUser.phone !== "undefined" && rawUser.phone !== "null"
          ? String(rawUser.phone).trim()
          : "";

      let cleanLocation = "";
      if (rawUser.location && rawUser.location !== "undefined" && rawUser.location !== "null") {
        if (typeof rawUser.location === "string") {
          cleanLocation = rawUser.location.trim();
        } else if (typeof rawUser.location === "object") {
          const loc: any = rawUser.location;
          const parts = [loc.city, loc.state, loc.country].filter(Boolean);
          cleanLocation = parts.length > 0 ? parts.join(", ") : (loc.address || "");
        }
      }

      const cleanBio =
        rawUser.bio && rawUser.bio !== "undefined" && rawUser.bio !== "null"
          ? String(rawUser.bio).trim()
          : "";

      const cleanFirst = rawUser.firstname && rawUser.firstname !== "undefined" ? rawUser.firstname : "";
      const cleanLast = rawUser.lastname && rawUser.lastname !== "undefined" ? rawUser.lastname : "";
      const cleanFullName = [cleanFirst, cleanLast].filter(Boolean).join(" ").trim();

      const profileData = {
        fullName: cleanFullName,
        email: rawUser.useremail || "",
        phone: cleanPhone,
        dateOfBirth: formattedDob,
        location: cleanLocation,
        bio: cleanBio,
        profileImage: rawUser.profileIcon && rawUser.profileIcon !== "undefined" ? rawUser.profileIcon : "",
        emailVerified: !!rawUser.emailVerified,
        _id: rawUser._id || "",
      };

      setProfile(profileData);
      setOriginalProfile({
        email: rawUser.useremail || "",
        phone: cleanPhone,
      });
    } catch (err: any) {
      console.error("Error fetching profile details:", err);
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    handleFetchDetails();
  }, []);

  // Update imagePreview when profile loads
  useEffect(() => {
    if (profile.profileImage) {
      setImagePreview(profile.profileImage);
    }
  }, [profile.profileImage]);

  const renderProfileTab = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          Profile Information
        </h1>
        <p className="text-teal-100">Update your personal details</p>
      </div>

      <form onSubmit={handleSubmit} className="p-8">
        {/* Profile Image Section */}
        <div className="flex items-center space-x-6 mb-8 pb-6 border-b border-gray-200">
          <div className="relative">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-teal-100 shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-teal-500 flex items-center justify-center border-4 border-teal-100 shadow-lg">
                <span className="text-white text-3xl font-bold">
                  {profile.fullName
                    .split(" ")
                    .map((name) => name[0])
                    .join("")
                    .toUpperCase()}
                </span>
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-teal-500 text-white p-2 rounded-full cursor-pointer hover:bg-teal-600 transition-colors shadow-lg">
              <Camera size={16} />
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-800">
              Profile Picture
            </h2>
            <p className="text-gray-600 text-sm">
              JPG, PNG, WEBP up to 5MB. Click camera or buttons below.
            </p>
            {profileImageFile && (
              <p className="text-sm text-teal-600 font-medium">
                Selected: {profileImageFile.name} (
                {Math.round(profileImageFile.size / 1024)} KB)
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <label className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 rounded-lg text-sm font-medium transition-colors cursor-pointer shadow-sm">
                <Camera size={15} />
                <span>Change Photo</span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>

              {(imagePreview || profile.profileImage || profileImageFile) && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={isRemovingImage}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
                  title="Remove profile photo"
                >
                  <Trash2 size={15} />
                  <span>{isRemovingImage ? "Removing..." : "Remove Photo"}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                value={profile.fullName}
                onChange={(e) => handleInputChange("fullName", e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                placeholder="Enter your full name"
              />
            </div>
          </div>

          {/* Email */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="email"
                value={profile.email}
                disabled
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                placeholder="Enter your email"
              />
            </div>
          </div>

          {/* Phone */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone
                className="absolute left-3 top-3 text-gray-400"
                size={20}
              />
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                maxLength={10}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                placeholder="Enter your 10-digit phone number"
              />
            </div>
          </div>

          {/* Date of Birth */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth
            </label>
            <div className="relative">
              <Calendar
                className="absolute left-3 top-3 text-gray-400"
                size={20}
              />
              <input
                type="date"
                value={profile.dateOfBirth}
                onChange={(e) =>
                  handleInputChange("dateOfBirth", e.target.value)
                }
                max={new Date().toISOString().split('T')[0]}
                min="1904-01-01"
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
              />
            </div>
          </div>

          {/* Location */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <div className="relative">
              <MapPin
                className="absolute left-3 top-3 text-gray-400"
                size={20}
              />
              <input
                type="text"
                value={profile.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                placeholder="Enter your location"
              />
            </div>
          </div>

          {/* Bio */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              value={profile.bio}
              onChange={(e) => handleInputChange("bio", e.target.value)}
              rows={4}
              maxLength={500}
              className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 resize-none"
              placeholder="Tell us about yourself and your yoga journey..."
            />
            <p className="text-sm text-gray-500 mt-1">
              {profile.bio.length}/500 characters
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
         <button>
            <Link
              to="/profile"
             className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"

            >
              Cancel
            </Link>
          </button>
          <button
            type="submit"
            className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-200 font-medium shadow-lg"
          >
            <Save size={20} />
            <span>Save Changes</span>
          </button>

          
        </div>
      </form>
    </div>
  );

  const renderPasswordTab = () => <ChangePassword />;

  return (
    <section className="w-full flex justify-center bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen py-8">
      <Toaster />
      <div className="w-[1200px] my-12">
        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex-1 flex items-center justify-center space-x-3 px-6 py-4 text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? `text-${tab.color}-600 border-b-2 border-${tab.color}-500 bg-${tab.color}-50`
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Icon size={20} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Page Title */}
          <div className="p-6 bg-gradient-to-r from-gray-600 to-gray-700">
            <h1 className="text-2xl font-bold text-white">Account Settings</h1>
            <p className="text-gray-200 text-sm mt-1">
              Manage your profile information and account security
            </p>
          </div>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-300">
          {activeTab === "profile" && renderProfileTab()}
          {activeTab === "password" && renderPasswordTab()}
        </div>
      </div>
    </section>
  );
}